import { Differ } from "json-diff-kit";
import moment from "moment";
import React, { useMemo } from "react";
import {
    Badge,
    Dropdown,
    ListGroupItem,
    OverlayTrigger,
    Tooltip,
} from "react-bootstrap";
import { BiSolidPencil } from "react-icons/bi";
import { FaExternalLinkAlt, FaFilter, FaQuestion } from "react-icons/fa";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { MdOutlineMoreVert } from "react-icons/md";
import { useNavigate } from "react-router";

import { useDocumentByPath } from "../../api/firestoreWrappers.generated.jsx";
import urls from "../../urls.jsx";
import { DiffViewer } from "./DiffViewer.jsx";

const differ = new Differ({
    detectCircular: true, // default `true`
    maxDepth: Infinity, // default `Infinity`
    showModifications: true, // default `true`
    arrayDiffMethod: "lcs", // default `"normal"`, but `"lcs"` may be more useful
    recursiveEqual: true,
});

function preprocessDoc(doc, collection) {
    if (!doc || typeof doc !== "object") return doc;

    const transformValue = (key, value) => {
        // --- routines specific ---
        if (collection === "routines") {
            if (key === "days" && Array.isArray(value) && value.length === 7) {
                const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                const result = days.filter((_, i) => value[i]);
                return result.length ? result.join(", ") : "None";
            }
        }

        return value;
    };

    const recurse = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(recurse);
        }

        if (obj && typeof obj === "object") {
            return Object.fromEntries(
                Object.entries(obj).map(([key, value]) => {
                    const transformed = transformValue(key, value);
                    return [key, recurse(transformed)];
                }),
            );
        }

        return obj;
    };

    return recurse(doc);
}

export default function LogListItem({
    log,
    timeDisplayFormat,
    filters,
    setFilters,
    users,
    forceSingleColumn,
}) {
    const date = moment(log.changedAt.toDate()).format(timeDisplayFormat);
    const navigate = useNavigate();

    const [logDocument] = useDocumentByPath(log?.docPath, { mode: "once" });

    const user = users?.[log?.authId];

    function OverlayTriggerContainer({ children }) {
        return (
            <OverlayTrigger
                delay={{ show: 500 }}
                overlay={
                    <Tooltip id={`log-tooltip-${log?.id}`}>
                        <span>by {user?.email || log?.authId}</span>
                    </Tooltip>
                }
                popperConfig={{
                    modifiers: [
                        { name: "offset", options: { offset: [0, 10] } },
                    ],
                }}
            >
                {children}
            </OverlayTrigger>
        );
    }

    const logInfo = useMemo(() => {
        const id = log?.docId;
        const collection = log?.collection;
        const name = logDocument?.name;

        switch (collection) {
            case "droneConfigs":
                return {
                    type: "drone",
                    name: id,
                    nav: urls.droneSettings(id),
                };

            case "routines": {
                return {
                    type: "routine",
                    name:
                        name ??
                        log?.diffs?.oldDoc?.name ??
                        log?.diffs?.newDoc?.name ??
                        id,
                    nav: urls.routineSettings(
                        log?.docPathSegments?.[1],
                        log?.docPathSegments?.[3],
                        log?.docPathSegments?.[5],
                    ),
                };
            }

            case "docks": {
                return {
                    type: "dock",
                    name:
                        name ??
                        log?.diffs?.oldDoc?.name ??
                        log?.diffs?.newDoc?.name ??
                        id,
                    nav: urls.dockSettings(
                        log?.docPathSegments?.[1],
                        log?.docPathSegments?.[3],
                        log?.docPathSegments?.[5],
                    ),
                };
            }

            case "apps": {
                return {
                    type: "app",
                    name:
                        name ??
                        log?.diffs?.oldDoc?.name ??
                        log?.diffs?.newDoc?.name ??
                        id,
                    nav: urls.appSettings(log?.docId),
                };
            }

            case "locations": {
                return {
                    type: "location",
                    name:
                        name ??
                        log?.diffs?.oldDoc?.name ??
                        log?.diffs?.newDoc?.name ??
                        id,
                    nav: urls.locationSettings(
                        log?.docPathSegments?.[1],
                        log?.docPathSegments?.[3],
                    ),
                };
            }

            case "tasks": {
                return {
                    type: "task",
                    name:
                        name ??
                        log?.diffs?.oldDoc?.name ??
                        log?.diffs?.newDoc?.name ??
                        id,
                    nav: urls.taskSettings(
                        log?.docPathSegments?.[1],
                        log?.docPathSegments?.[3],
                        log?.docPathSegments?.[5],
                    ),
                };
            }

            case "payloads": {
                return {
                    type: "payload",
                    name:
                        name ??
                        log?.diffs?.oldDoc?.name ??
                        log?.diffs?.newDoc?.name ??
                        id,
                    nav: urls.payloadSettings(id),
                };
            }

            default:
                return { type: collection, name: name ?? id };
        }
    }, [log, logDocument]);

    function getColor() {
        switch (log?.action) {
            case "update":
                return "primary";
            case "delete":
                return "danger";
            case "create":
                return "success";
            default:
                return "warning";
        }
    }

    function getBadge() {
        switch (log?.action) {
            case "create":
                return <FaPlus />;
            case "delete":
                return <FaMinus />;
            case "update":
                return <BiSolidPencil />;
            default:
                return <FaQuestion />;
        }
    }

    const CustomToggle = React.forwardRef(({ onClick }, ref) => (
        <button
            type="button"
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="p-0 bg-transparent border-0 d-flex h-100"
        >
            <MdOutlineMoreVert size={20} />
        </button>
    ));
    CustomToggle.displayName = "CustomToggle";

    function filterElement() {
        switch (log?.collection) {
            case "droneConfigs":
                setFilters({
                    ...filters,
                    droneId: log?.docPathSegments[1],
                });
                break;
            case "routines":
                setFilters({
                    ...filters,
                    organizationId: log?.docPathSegments[1],
                    locationId: log?.docPathSegments[3],
                    routineId: log?.docPathSegments[5],
                });
                break;
            case "apps":
                setFilters({
                    ...filters,
                    appId: log?.docPathSegments[1],
                });
                break;
            case "docks":
                setFilters({
                    ...filters,
                    organizationId: log?.docPathSegments[1],
                    locationId: log?.docPathSegments[3],
                    dockId: log?.docPathSegments[5],
                });
                break;
            case "locations":
                setFilters({
                    ...filters,
                    organizationId: log?.docPathSegments[1],
                    locationId: log?.docPathSegments[3],
                });
                break;
            case "tasks":
                setFilters({
                    ...filters,
                    organizationId: log?.docPathSegments[1],
                    locationId: log?.docPathSegments[3],
                    taskId: log?.docPathSegments[5],
                });
                break;
            case "payloads":
                setFilters({
                    ...filters,
                    payloadId: log?.docPathSegments[1],
                });
                break;
        }
    }

    const isUpdate = log?.action === "update";
    const isCreate = log?.action === "create";
    const isDelete = log?.action === "delete";

    const { oldDoc, newDoc } = React.useMemo(() => {
        if (!log?.diffs || !log?.action) return { oldDoc: null, newDoc: null };

        if (log.action === "update") {
            return {
                oldDoc: Object.fromEntries(
                    Object.entries(log.diffs).map(([k, v]) => [k, v.old]),
                ),
                newDoc: Object.fromEntries(
                    Object.entries(log.diffs).map(([k, v]) => [k, v.new]),
                ),
            };
        }

        if (log.action === "create") {
            return { oldDoc: {}, newDoc: log.diffs.newDoc };
        }

        if (log.action === "delete") {
            return { oldDoc: log.diffs.oldDoc, newDoc: {} };
        }

        return { oldDoc: null, newDoc: null };
    }, [log?.action, log?.diffs]);

    const diff = React.useMemo(() => {
        if (oldDoc === null || newDoc === null) return null;

        const processedOld = preprocessDoc(oldDoc, log?.collection);
        const processedNew = preprocessDoc(newDoc, log?.collection);

        return differ.diff(processedOld, processedNew);
    }, [oldDoc, newDoc, log?.collection]);

    return (
        <>
            <ListGroupItem
                variant="flush"
                className="px-2 pt-1 pb-0 border-0 my-2"
            >
                <OverlayTriggerContainer>
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex placeholder-glow">
                            <Badge
                                pill
                                bg={getColor()}
                                className="me-2 d-flex align-items-center"
                            >
                                {getBadge()}
                            </Badge>
                            {logInfo?.name ?? (
                                <span
                                    className="placeholder rounded"
                                    style={{ width: 120 }}
                                />
                            )}
                        </div>
                        <div className="d-flex align-items-center">
                            <Badge
                                pill
                                bg="secondary"
                                className="d-flex align-items-center"
                            >
                                {date}
                            </Badge>
                            {(logInfo?.type || logInfo?.nav) && (
                                <div>
                                    <Dropdown align="end">
                                        <Dropdown.Toggle as={CustomToggle} />
                                        <Dropdown.Menu>
                                            {logInfo?.type && (
                                                <Dropdown.Item
                                                    onClick={() =>
                                                        filterElement()
                                                    }
                                                >
                                                    <FaFilter className="me-2" />
                                                    {`Filter on this ${logInfo.type}`}
                                                </Dropdown.Item>
                                            )}
                                            {logInfo?.nav && (
                                                <Dropdown.Item
                                                    onClick={() =>
                                                        navigate(logInfo.nav)
                                                    }
                                                >
                                                    <FaExternalLinkAlt className="me-2" />
                                                    {`Go to settings page`}
                                                </Dropdown.Item>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            )}
                        </div>
                    </div>
                </OverlayTriggerContainer>
            </ListGroupItem>

            {diff && (
                <DiffViewer
                    diff={diff}
                    isCreate={isCreate}
                    isUpdate={isUpdate}
                    isDelete={isDelete}
                    forceSingleColumn={forceSingleColumn}
                />
            )}
        </>
    );
}
